import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { NotificationFilter } from '@/lib/types';
import { userSettingsQueryOptions, useUpdateUserSettingsMutation } from '@/lib/query-options';

export interface NotificationFilterState extends NotificationFilter {
  sources: string[];
  warningLevels: string[];
  searchQuery?: string;
}

export function useNotificationFilters(organizationId?: number) {
  const { data: settings } = useQuery(userSettingsQueryOptions());
  const settingsMutation = useUpdateUserSettingsMutation();
  const initialized = useRef(false);

  const [filters, setFilters] = useState<NotificationFilterState>(() => ({
    organization_id: organizationId,
    is_archived: undefined,
    sources: [],
    warningLevels: [],
    start_date: undefined,
    end_date: undefined,
    searchQuery: undefined,
  }));

  // Initialize from persisted settings on first load
  useEffect(() => {
    if (settings && !initialized.current) {
      const saved = settings.filters?.notifications;
      if (saved) {
        setFilters((prev) => ({
          ...prev,
          sources: saved.sources || [],
          warningLevels: saved.warning_levels || [],
        }));
      }
      initialized.current = true;
    }
  }, [settings]);

  // Count active filters (excluding organization_id and searchQuery)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.is_archived !== undefined) count++;
    if (filters.sources && filters.sources.length > 0) count++;
    if (filters.warningLevels && filters.warningLevels.length > 0) count++;
    if (filters.start_date || filters.end_date) count++;
    // Note: searchQuery is not counted as an "active filter" for the filter badge
    return count;
  }, [filters]);

  // Convert filter state to API filter format with multi-select support
  const apiFilters = useMemo<NotificationFilter>(() => {
    const filter: NotificationFilter = {
      organization_id: filters.organization_id,
      is_archived: filters.is_archived,
      start_date: filters.start_date,
      end_date: filters.end_date,
      search_query: filters.searchQuery,
    };

    // Multi-select support: pass full arrays to API
    if (filters.sources && filters.sources.length > 0) {
      filter.sources = filters.sources;
    }
    if (filters.warningLevels && filters.warningLevels.length > 0) {
      filter.warning_levels = filters.warningLevels;
    }

    return filter;
  }, [filters]);

  const updateFilters = useCallback(
    (updates: Partial<NotificationFilterState>) => {
      setFilters((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  const toggleSource = useCallback(
    (source: string) => {
      const sources = filters.sources || [];
      const newSources = sources.includes(source) ? sources.filter((s) => s !== source) : [...sources, source];
      updateFilters({ sources: newSources });
    },
    [filters.sources, updateFilters]
  );

  const toggleWarningLevel = useCallback(
    (level: string) => {
      const warningLevels = filters.warningLevels || [];
      const newLevels = warningLevels.includes(level)
        ? warningLevels.filter((l) => l !== level)
        : [...warningLevels, level];
      updateFilters({ warningLevels: newLevels });
    },
    [filters.warningLevels, updateFilters]
  );

  const setDateRange = useCallback(
    (start_date: string | undefined, end_date: string | undefined) => {
      updateFilters({ start_date, end_date });
    },
    [updateFilters]
  );

  const setSearchQuery = useCallback(
    (searchQuery: string | undefined) => {
      updateFilters({ searchQuery });
    },
    [updateFilters]
  );

  const clearFilters = useCallback(() => {
    setFilters({
      organization_id: organizationId,
      sources: [],
      warningLevels: [],
      is_archived: undefined,
      start_date: undefined,
      end_date: undefined,
      searchQuery: undefined,
    });
  }, [organizationId]);

  // Apply multiple filter changes at once (used by FilterDropdown)
  const applyFilters = useCallback(
    (filterUpdates: {
      sources?: string[];
      warningLevels?: string[];
      startDate?: string;
      endDate?: string;
    }) => {
      updateFilters({
        sources: filterUpdates.sources,
        warningLevels: filterUpdates.warningLevels,
        start_date: filterUpdates.startDate,
        end_date: filterUpdates.endDate,
      });

      // Persist only sources and warning_levels (dates excluded per spec)
      // Fall back to current state for unspecified fields to avoid erasing saved values
      settingsMutation.mutate({
        filters: {
          notifications: {
            sources: filterUpdates.sources ?? filters.sources ?? [],
            warning_levels: filterUpdates.warningLevels ?? filters.warningLevels ?? [],
          },
        },
      });
    },
    [updateFilters, settingsMutation]
  );

  // Load filters from a preset (used by PresetSelector)
  const loadPresetFilters = useCallback(
    (presetFilters: Omit<NotificationFilterState, 'organization_id'>) => {
      updateFilters(presetFilters);
    },
    [updateFilters]
  );

  return {
    filters,
    apiFilters,
    activeFilterCount,
    updateFilters,
    toggleSource,
    toggleWarningLevel,
    setDateRange,
    setSearchQuery,
    clearFilters,
    applyFilters,
    loadPresetFilters,
  };
}
